import CheckIcon from "@mui/icons-material/Check"
import CloseIcon from "@mui/icons-material/Close"
import { Grid, Paper, Tooltip } from "@mui/material"
import React, { useCallback, useContext, useEffect, useMemo, useState } from "react"
import { customPalette } from "../../../../../../../../../../../../theme"
import { SquareBadge } from "../../../../../../../../../../../components/badges/SquareBadge"
import { IconPencil } from "../../../../../../../../../../../components/custom-icons/icon-pencil"
import { IconTrash } from "../../../../../../../../../../../components/custom-icons/icon-trash"
import { FormNumberInput } from "../../../../../../../../../../../components/inputs/number-input/FormNumberInput"
import { SelectObjectInput } from "../../../../../../../../../../../components/inputs/select-input/SelectObjectInput"
import { NonEditableTextField } from "../../../../../../../../../../../components/inputs/text-input/NonEditableTextField"
import { AssignedParking } from "../../../../../../../../../../../core/dto/beem-shot/BSBuildingLevel/AssignedParking"
import { BSParking } from "../../../../../../../../../../../core/dto/beem-shot/BSParking/BSParking"
import { stringToNumber } from "../../../../../../../../../../../core/services/helper-service"
import { BSParkingListContext } from "../../../../BSBuildingParkingPage/context/BSParkingListContext"
import { BSLevelContext } from "../../../context/BSLevelContext"
import { GroupLevel } from "../../../context/GroupLevel"
import { ParkingAssignmentContext } from "../../../context/ParkingAssignmentContext"
import { BSLevelFormContext } from "../../../context/BSLevelFormContext"

interface IAssignedParkingForm {
  parkingId: string
  percentage: number
}

function dtoToForm(assignedParking: AssignedParking): IAssignedParkingForm {
  return {
    parkingId: assignedParking.parkingId,
    percentage: assignedParking.percentage,
  }
}

function formToDto(form: IAssignedParkingForm): AssignedParking {
  return {
    parkingId: form.parkingId,
    percentage: form.percentage,
  }
}

interface IProps {
  groupLevel: GroupLevel
  assignedParking: AssignedParking
  parkingIndex: number
  displayAssignmentParkingForm: boolean
  hasAllSuperstructureParkingAssigned: boolean
  parkingPercentageSum: number
}

export function ParkingAssignedInfos({
  groupLevel,
  assignedParking,
  parkingIndex,
  displayAssignmentParkingForm,
  hasAllSuperstructureParkingAssigned,
  parkingPercentageSum,
}: Readonly<IProps>): React.JSX.Element {
  const {
    superstructureOptions,
    infrastructureOptions,
    fieldValidators,
    parkingErrors,
    setParkingErrors,
    setIsEditing,
    validateParkingAssignment,
  } = useContext(ParkingAssignmentContext)
  const { superstructureParkingList, infrastructureParkingList } = useContext(BSParkingListContext)
  const { setIsLevelUpdated } = useContext(BSLevelContext)
  const { setSelectedGroupLevel } = useContext(BSLevelFormContext)
  const { setFormFieldValue } = useContext(BSLevelFormContext)

  const [isLocalEdit, setIsLocalEdit] = useState<boolean>(false)
  const [assignedParkingForm, setAssignedParkingForm] = useState<IAssignedParkingForm>(dtoToForm(assignedParking))

  const isSuperstructure = useMemo(() => groupLevel.levelList[0].superstructure, [groupLevel.levelList])

  const bsParking: BSParking | undefined = useMemo(
    () =>
      isSuperstructure
        ? superstructureParkingList.find((parking) => parking.id === assignedParking.parkingId)
        : infrastructureParkingList.find((parking) => parking.id === assignedParking.parkingId),
    [isSuperstructure, superstructureParkingList, infrastructureParkingList, assignedParking.parkingId]
  )

  const bsParkingForm: BSParking | undefined = useMemo(
    () =>
      isSuperstructure
        ? superstructureParkingList.find((parking) => parking.id === assignedParkingForm.parkingId)
        : infrastructureParkingList.find((parking) => parking.id === assignedParkingForm.parkingId),
    [isSuperstructure, superstructureParkingList, infrastructureParkingList, assignedParkingForm.parkingId]
  )

  const assignedParkingName = useMemo(() => (bsParking ? bsParking.name : undefined), [bsParking])

  const handleEditParking: () => void = useCallback(() => {
    setSelectedGroupLevel(groupLevel)
    setIsLocalEdit(true)
    setIsEditing(true)
  }, [groupLevel, setIsEditing, setSelectedGroupLevel])

  const handleCancelUpdate: () => void = useCallback(() => {
    setAssignedParkingForm(dtoToForm(assignedParking))
    setSelectedGroupLevel(undefined)
    setIsLocalEdit(false)
    setIsEditing(false)
    setParkingErrors({})
  }, [assignedParking, setIsEditing, setParkingErrors, setSelectedGroupLevel])

  const handleConfirmUpdates: () => void = useCallback(() => {
    const temporarySumPercentage = parkingPercentageSum - assignedParking.percentage
    if (assignedParkingForm && validateParkingAssignment(assignedParkingForm, temporarySumPercentage)) {
      const updatedAffectedParkingList = (groupLevel.levelList[0].bsParkingList ?? []).map((parking, index) => {
        if (index !== parkingIndex) {
          return parking
        }
        return { ...formToDto(assignedParkingForm) }
      })

      setFormFieldValue("bsParkingList", updatedAffectedParkingList, groupLevel.id, isSuperstructure)

      setSelectedGroupLevel(undefined)
      setIsLocalEdit(false)
      setIsEditing(false)
      setIsLevelUpdated(true)
    }
  }, [
    parkingPercentageSum,
    assignedParking.percentage,
    assignedParkingForm,
    validateParkingAssignment,
    groupLevel.levelList,
    groupLevel.id,
    setFormFieldValue,
    isSuperstructure,
    setSelectedGroupLevel,
    setIsEditing,
    setIsLevelUpdated,
    parkingIndex,
  ])

  const handleFieldChange: (field: keyof AssignedParking, value: number | string) => void = useCallback(
    (field, value) => {
      setAssignedParkingForm((prev) => {
        if (!prev) {
          return prev
        }

        return { ...prev, [field]: value }
      })

      fieldValidators[field]?.(field, value, parkingPercentageSum)
    },
    [fieldValidators, parkingPercentageSum]
  )

  const handleUnlinkParking: () => void = useCallback(() => {
    setIsLevelUpdated(true)
    const updatedAffectedParkingList = (groupLevel.levelList[0].bsParkingList ?? []).filter(
      (parking) => parking !== assignedParking
    )

    setFormFieldValue("bsParkingList", updatedAffectedParkingList, groupLevel.id, isSuperstructure)
  }, [assignedParking, setIsLevelUpdated, groupLevel, isSuperstructure, setFormFieldValue])

  // update parking percentage field in form only for infrastructure levels
  useEffect(() => {
    if (!isSuperstructure) {
      setAssignedParkingForm((prev) => prev && { ...prev, percentage: 100 })
    }
  }, [isSuperstructure, assignedParkingForm])

  return (
    <Paper elevation={0} sx={{ m: 1 }}>
      <Grid container alignItems="center" columnSpacing={1}>
        <Grid item container columnSpacing={1} xs={11} borderRadius={4} p={1} boxShadow="0px 1px 3px rgba(0, 0, 0, 0.1)">
          {isLocalEdit ? (
            <>
              <Grid item xs={8}>
                {isSuperstructure && hasAllSuperstructureParkingAssigned ? (
                  <Tooltip title="Tous les parkings sont affectés." placement="bottom-end" arrow>
                    <span style={{ width: "100%" }}>
                      <NonEditableTextField label="Parking" value={assignedParkingName} />
                    </span>
                  </Tooltip>
                ) : (
                  <SelectObjectInput
                    fieldName="parkingId"
                    label="Parking à affecter"
                    selectedOption={bsParkingForm}
                    options={isSuperstructure ? superstructureOptions : infrastructureOptions}
                    handleChange={(value) => value?.id && handleFieldChange("parkingId", value.id)}
                    errors={parkingErrors}
                  />
                )}
              </Grid>
              <Grid item xs={4}>
                <Tooltip title="par rapport à la surface du groupe" arrow placement="right">
                  <div>
                    <FormNumberInput
                      fieldName="percentage"
                      label="Pourcentage (%)"
                      value={isSuperstructure ? assignedParkingForm?.percentage ?? 0 : 100}
                      handleChange={(value) => handleFieldChange("percentage", stringToNumber(value))}
                      errors={parkingErrors}
                      disabled={!isSuperstructure}
                    />
                  </div>
                </Tooltip>
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={8}>
                <NonEditableTextField label="Parking" value={assignedParkingName} />
              </Grid>

              <Grid item xs={4}>
                <Tooltip title="par rapport à la surface du groupe" arrow placement="right">
                  <div>
                    <NonEditableTextField label="Pourcentage (%)" value={assignedParking.percentage} />
                  </div>
                </Tooltip>
              </Grid>
            </>
          )}
        </Grid>

        <Grid item container xs={1} columnSpacing={1}>
          {isLocalEdit ? (
            <>
              <Grid item xs={6}>
                <SquareBadge
                  onClick={handleCancelUpdate}
                  icon={<CloseIcon color="error" />}
                  backgroundColor="rgba(255, 46, 46, 0.1)"
                  cursor="pointer"
                />
              </Grid>
              <Grid item xs={6}>
                <SquareBadge
                  onClick={handleConfirmUpdates}
                  icon={<CheckIcon color="success" />}
                  backgroundColor="rgba(46, 125, 50, 0.1)"
                  cursor="pointer"
                />
              </Grid>
            </>
          ) : (
            <>
              <Grid item xs={isSuperstructure ? 6 : 12}>
                <SquareBadge
                  onClick={handleEditParking}
                  icon={
                    <IconPencil
                      sx={{ color: displayAssignmentParkingForm ? customPalette.lightGray2 : customPalette.lightBlue2 }}
                    />
                  }
                  backgroundColor={displayAssignmentParkingForm ? customPalette.lightGray5 : customPalette.lightGray7}
                  cursor={displayAssignmentParkingForm ? "default" : "pointer"}
                  disabled={displayAssignmentParkingForm}
                />
              </Grid>
              {isSuperstructure && (
                <Grid item xs={6}>
                  <SquareBadge
                    onClick={handleUnlinkParking}
                    icon={
                      <IconTrash
                        sx={{ color: displayAssignmentParkingForm ? customPalette.lightGray2 : customPalette.lightBlue2 }}
                      />
                    }
                    backgroundColor={displayAssignmentParkingForm ? customPalette.lightGray5 : customPalette.lightGray7}
                    cursor={displayAssignmentParkingForm ? "default" : "pointer"}
                    disabled={displayAssignmentParkingForm}
                  />
                </Grid>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </Paper>
  )
}
